import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Eraser } from 'lucide-react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { sanitizeRichHtml } from '../../../../utils/sanitize';

interface MarkdownEditorProps {
    initialValue: string;
    onSave: (value: string) => void;
    onDraftChange?: (value: string) => void;
    onCancel?: () => void;
    maxLength?: number;
    onGenerateAISummary?: () => Promise<string>;
}

type FormatCommand = 'bold' | 'italic' | 'underline' | 'insertUnorderedList' | 'insertOrderedList' | 'removeFormat';

const TOOLBAR: { command: FormatCommand; label: string; Icon: React.ComponentType<{ className?: string }> }[] = [
    { command: 'bold', label: 'Negrito', Icon: Bold },
    { command: 'italic', label: 'Itálico', Icon: Italic },
    { command: 'underline', label: 'Sublinhado', Icon: Underline },
    { command: 'insertUnorderedList', label: 'Lista', Icon: List },
    { command: 'insertOrderedList', label: 'Lista numerada', Icon: ListOrdered },
    { command: 'removeFormat', label: 'Limpar formatação', Icon: Eraser },
];

const shellClass =
    'overflow-hidden rounded-lg border border-border bg-surface-subtle';

const fieldClass =
    'min-h-24 w-full bg-surface-0 px-s3 py-s2 font-sans text-base leading-normal text-default outline-none [&_ul]:list-disc [&_ul]:pl-s5 [&_ol]:list-decimal [&_ol]:pl-s5 [&_p]:mb-s2 [&_strong]:font-semibold';

const toolbarBtnClass =
    'btn-icon btn-icon-sm toolbar-icon-btn min-h-11 min-w-11 touch-manipulation md:min-h-8 md:min-w-8';

export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
    initialValue,
    onSave,
    onDraftChange,
    onCancel,
    maxLength = 5000,
    onGenerateAISummary,
}) => {
    const [value, setValue] = useState(() => sanitizeRichHtml(initialValue));
    const [isEditing, setIsEditing] = useState(false);
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);
    const editorRef = useRef<HTMLDivElement>(null);
    const hintId = useId();

    useEffect(() => {
        if (!isEditing) {
            setValue(sanitizeRichHtml(initialValue));
        }
    }, [initialValue, isEditing]);

    const readEditorHtml = useCallback((): string => {
        const raw = editorRef.current?.innerHTML ?? '';
        return sanitizeRichHtml(raw);
    }, []);

    const commitEditorHtml = useCallback(() => {
        const sanitized = readEditorHtml();
        setValue(sanitized);
        onDraftChange?.(sanitized);
    }, [readEditorHtml, onDraftChange]);

    const syncEditorDom = useCallback((html: string) => {
        if (!editorRef.current) return;
        const sanitized = sanitizeRichHtml(html);
        if (editorRef.current.innerHTML !== sanitized) {
            editorRef.current.innerHTML = sanitized;
        }
    }, []);

    useEffect(() => {
        if (!isEditing) return;
        if (document.activeElement === editorRef.current) return;
        syncEditorDom(value);
    }, [isEditing, value, syncEditorDom]);

    const runFormat = (command: FormatCommand) => {
        editorRef.current?.focus();
        document.execCommand(command, false);
        commitEditorHtml();
    };

    const handleSave = () => {
        const sanitized = readEditorHtml();
        const plainLength = editorRef.current?.textContent?.length ?? sanitized.length;
        if (plainLength > maxLength) return;
        onSave(sanitized);
        setValue(sanitized);
        setIsEditing(false);
    };

    const handleCancel = () => {
        onCancel?.();
        setValue(sanitizeRichHtml(initialValue));
        setIsEditing(false);
    };

    const appendHtml = (fragment: string) => {
        const current = value || '';
        const separator = current && !current.endsWith('<p><br></p>') ? '<p><br></p>' : '';
        const next = sanitizeRichHtml(current + separator + fragment.trim());
        setValue(next);
        setIsEditing(true);
    };

    const handleAISummaryClick = async () => {
        if (onGenerateAISummary) {
            setIsGeneratingAI(true);
            try {
                const generatedContent = await onGenerateAISummary();
                appendHtml(generatedContent);
            } catch (error) {
                console.error('Erro ao gerar resumo IA:', error);
            } finally {
                setIsGeneratingAI(false);
            }
            return;
        }

        appendHtml(`
<p><strong>✨ Resumo da Negociação (IA)</strong></p>
<p><strong>Perfil:</strong> </p>
<p><strong>Produto apresentado:</strong> </p>
<p><strong>Situação atual:</strong> </p>
<p><strong>Temperatura:</strong> </p>
<p><strong>Próximo passo:</strong> </p>
<p><strong>Retornar em:</strong> </p>
        `);
    };

    const strippedLength = value.replace(/<[^>]*>/g, '').length;
    const charCount = editorRef.current?.textContent?.length ?? strippedLength;
    const isOverLimit = charCount > maxLength;
    const beginEdit = () => setIsEditing(true);

    return (
        <div className="input-wrap">
            {!isEditing && (
                <button
                    type="button"
                    onClick={beginEdit}
                    className={
                        strippedLength === 0
                            ? 'chip chip--dashed min-h-11 w-full justify-center rounded-lg'
                            : `${shellClass} ${fieldClass} cursor-text text-left`
                    }
                    aria-label={strippedLength === 0 ? 'Adicionar Observação' : 'Editar observações'}
                >
                    {strippedLength === 0 ? (
                        <>
                            <AppIcon name="plus" className="h-4 w-4" aria-hidden="true" />
                            <span>Adicionar Observação</span>
                        </>
                    ) : (
                        <span
                            className="block"
                            dangerouslySetInnerHTML={{ __html: value }}
                        />
                    )}
                </button>
            )}

            {isEditing && (
                <div className={`${shellClass} ${isOverLimit ? 'border-danger' : 'focus-within:border-primary-light'} focus-within:shadow-[var(--shadow-focus)]`}>
                    <div
                        role="toolbar"
                        aria-label="Formatação"
                        className="flex flex-wrap items-center gap-s1 border-b border-border px-s2 py-s1"
                    >
                        {TOOLBAR.map(({ command, label, Icon }) => (
                            <button
                                key={command}
                                type="button"
                                title={label}
                                aria-label={label}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => runFormat(command)}
                                className={toolbarBtnClass}
                            >
                                <Icon className="size-4" aria-hidden />
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={handleAISummaryClick}
                            disabled={isGeneratingAI}
                            className="ml-auto inline-flex min-h-11 touch-manipulation items-center gap-s1 rounded-md px-s2 text-caption font-medium text-primary hover:bg-primary-subtle disabled:cursor-not-allowed disabled:opacity-50 md:min-h-8"
                            title="Gerar estrutura de resumo com inteligência artificial"
                        >
                            {isGeneratingAI ? (
                                <AppIcon name="loader" className="size-3.5 animate-spin" aria-hidden />
                            ) : (
                                <AppIcon name="sparkles" className="size-3.5" aria-hidden />
                            )}
                            <span>{isGeneratingAI ? 'Analisando…' : 'Resumo IA'}</span>
                        </button>
                    </div>

                    <div
                        ref={editorRef}
                        contentEditable
                        suppressContentEditableWarning
                        role="textbox"
                        aria-multiline="true"
                        aria-label="Observações"
                        aria-describedby={hintId}
                        aria-invalid={isOverLimit}
                        onInput={commitEditorHtml}
                        onFocus={() => syncEditorDom(value)}
                        className={fieldClass}
                    />

                    <div className="flex items-center gap-s2 border-t border-border px-s2 py-s2">
                        <p
                            id={hintId}
                            className={`input-hint min-w-0 flex-1 font-mono tabular-nums ${isOverLimit ? 'input-error' : ''}`}
                        >
                            {charCount}/{maxLength}
                        </p>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="inline-flex min-h-11 shrink-0 touch-manipulation items-center rounded-md border border-border bg-transparent px-s3 text-sm font-medium text-secondary hover:bg-surface-subtle hover:text-default md:min-h-9"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            className="inline-flex min-h-11 shrink-0 touch-manipulation items-center rounded-md bg-primary px-s3 text-sm font-medium text-primary-foreground hover:bg-primary-dark md:min-h-9"
                        >
                            Salvar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
